/* eslint formatjs/no-literal-string-in-jsx: "error" */
import { Box, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { FlexContainer, Form, Stack as LegacyStack } from "@carrotfertility/carotene"
import { FormattedMessage, useIntl } from "react-intl"
import React, { useCallback, useEffect } from "react"
import {
  WhatKindOfJourneyHeaderText,
  JustForNowCoreText,
  JustForNowProText,
  AccessCareText
} from "../../../carrot-plans/steps/journey/WhatKindOfJourneyStep"
import { FormRadioButton, FormRadioButtonWithInput, FormRadioGroup } from "features/legacy-components/FormRadioGroup"
import { useJourneyExperiments, useTrackExperimentEvent } from "./hooks/useExperiments"
import { RegistrationFlowLayout } from "./RegistrationLayouts"
import { SkipButton, ContinueButton, RegistrationButtonFooter } from "./RegistrationButtons"
import { getHeap } from "utils/heap"

interface WhatKindOfJourneyProps {
  journey: string | null
  setJourney: (journey: string) => void
  somethingElseJourney: string | null
  setSomethingElseJourney: (somethingElseJourney: string) => void
  journeyOptions: any
  isCarrotCore: boolean
  skipped: boolean
  setSkipped: (skipped: boolean) => void
  onBack: () => void
  onComplete: () => void
}

function FormRadioButtons({ journey, somethingElseJourney, journeyOptions }): JSX.Element {
  const intl = useIntl()

  return (
    <FormRadioGroup
      name="journey"
      useFieldOptions={{
        defaultValue: journey
      }}
    >
      <LegacyStack direction="column" spacing=".25rem">
        {journeyOptions.map(({ value, label }) => (
          <FlexContainer key={value} justifyContent="flex-end">
            {value === "SOMETHING_ELSE" ? (
              <FormRadioButtonWithInput
                value={value}
                label={intl.formatMessage(label)}
                inputName="somethingElseJourney"
                inputUseFieldOptions={{ defaultValue: somethingElseJourney }}
              />
            ) : (
              <FormRadioButton value={value} label={intl.formatMessage(label)} />
            )}
          </FlexContainer>
        ))}
      </LegacyStack>
    </FormRadioGroup>
  )
}

export function WhatKindOfJourney({
  journey,
  setJourney,
  somethingElseJourney,
  setSomethingElseJourney,
  journeyOptions,
  isCarrotCore,
  setSkipped,
  onBack,
  onComplete
}: WhatKindOfJourneyProps): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const { journeySelectionInRegistrationExperiment } = useJourneyExperiments()
  const { trackExperimentEvent } = useTrackExperimentEvent()

  const trackPageView = useCallback(
    () => getHeap().track("JourneySelectionRegFlow", { cohort: journeySelectionInRegistrationExperiment }),
    [journeySelectionInRegistrationExperiment]
  )

  const onSubmit = (formValues) => {
    if (formValues.journey?.length) {
      setJourney(formValues.journey)
      setSkipped(false)
      trackExperimentEvent("registration-journey-selected")
    } else {
      setJourney(null)
      setSkipped(true)
    }
    setSomethingElseJourney(formValues.somethingElseJourney)
    onComplete()
  }

  useEffect(() => trackPageView(), [trackPageView])

  return (
    <RegistrationFlowLayout
      header={intl.formatMessage(WhatKindOfJourneyHeaderText)}
      description={
        <Stack
          spacing={theme.spacing(theme.tokens.spacing.xs)}
          marginTop={(theme) => theme.spacing(theme.tokens.spacing.xs)}
        >
          <p>
            {isCarrotCore ? (
              <Typography>{intl.formatMessage(JustForNowCoreText)}</Typography>
            ) : (
              <Typography>{intl.formatMessage(JustForNowProText)}</Typography>
            )}
          </p>
          <p>
            <Typography>{intl.formatMessage(AccessCareText)}</Typography>
          </p>
        </Stack>
      }
      interactiveElements={
        <Form onSubmit={(formValues) => onSubmit(formValues)}>
          <Box marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
            <FormRadioButtons
              journey={journey}
              somethingElseJourney={somethingElseJourney}
              journeyOptions={journeyOptions}
            />
            <RegistrationButtonFooter
              onBack={onBack}
              skipButton={
                <SkipButton onClick={() => onSubmit({})}>
                  <FormattedMessage defaultMessage="Skip for now" />
                </SkipButton>
              }
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
            />
          </Box>
        </Form>
      }
    />
  )
}
